import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';

import {
  fetchCarHouses,
  addCarHouse,
  editCarHouse,
  removeCarHouse,
  addCredit,
} from '../store/actions/carHouse-actions';
import { carHouseActions } from '../store/slices/carHouse-slice';
import CarHouseItem from '../components/CarHouse/CarHouseItem';
import CarHouseForm from '../components/CarHouse/CarHouseForm';
import PageHeader from '../components/UI/PageHeader/PageHeader';
import Spinner from '../components/UI/Spinner/Spinner';
import CreditModal from '../components/Subscriptions/CreditModal';

import { List, message, Form, Select, Input, Row, Col, Button } from 'antd';

const { Option } = Select;

const CarHouses = () => {
  const { t } = useTranslation();
  const { carHouses, isLoading, total, currentPage, searchParams } =
    useSelector((state) => state.carHouse);
  const [open, setOpen] = useState(false);
  const [openCreditModal, setOpenCreditModal] = useState(false);
  const [selectedCarHouse, setSelectedCarHouse] = useState(null);

  const [pageSize, setPageSize] = useState(8);

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [creditForm] = Form.useForm();

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const pagination = { page: currentPage, total: pageSize };
    const options = {
      params: searchParams,
      signal,
    };

    if (Object.keys(searchParams).length > 0) {
      form.setFieldsValue(searchParams);
    }

    dispatch(fetchCarHouses({ pagination, options }));

    return () => controller.abort();
  }, [dispatch, currentPage, pageSize, searchParams, form]);

  const deleteCarHouse = async (id) => {
    try {
      await dispatch(removeCarHouse(id));
      message.success(t('carHouses.removeSuccess'));
    } catch (err) {
      console.log(err);
      message.error(t('common.error'));
    }
  };
  const onAddCarHouse = () => {
    setSelectedCarHouse(null);
    setOpen(true);
  };

  const onAddCarHouseCredit = (carHouse) => {
    setSelectedCarHouse(carHouse);
    setOpenCreditModal(true);
  };

  const onEditCarHouse = (carHouse) => {
    //const address = carHouse.address.split(',');
    let selectedCarHouse = {
      id: carHouse.id,
      name: carHouse.name,
      phone: carHouse.phone,
      address: carHouse.address,
      //city: address[1],
      //country: address[2],
      email: carHouse.supportEmails[0],
    };
    setSelectedCarHouse(selectedCarHouse);
    setOpen(true);
  };

  const addCarHouseHandler = async (body) => {
    try {
      const resultAction = await dispatch(addCarHouse(body));

      if (addCarHouse.fulfilled.match(resultAction)) {
        message.success(t('carHouses.addSuccess'));
        setOpen(false);
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage = error?.description || t('common.error');

        if (error?.errorCode === 4090003) {
          message.error(t('carHouses.carHouseExists', { name: body.name }));
        } else {
          message.error(errorMessage);
        }
      }
    } catch (err) {
      message.error(t('common.unexpectedError'));
    }
  };

  const editCarHouseHandler = async (body) => {
    try {
      const resultAction = await dispatch(
        editCarHouse({ body, carHouseId: selectedCarHouse.id })
      );

      if (editCarHouse.fulfilled.match(resultAction)) {
        message.success(t('carHouses.editSuccess'));
        setOpen(false);
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage = error?.description || t('common.error');

        if (error?.errorCode === 4090003) {
          message.error(t('carHouses.carHouseExists', { name: body.name }));
        } else {
          message.error(errorMessage);
        }
      }
    } catch (err) {
      message.error(t('common.unexpectedError'));
    }
  };

  const addCarHouseCreditHandler = async (body, carHouseId) => {
    try {
      const resultAction = await dispatch(addCredit({ body, carHouseId }));

      if (addCredit.fulfilled.match(resultAction)) {
        message.success(t('carHouses.creditAddSuccess'));
        setOpenCreditModal(false);
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage = error?.description || 'Failed to add credits';
        message.error(errorMessage);
      }
    } catch (err) {
      message.error('Unexpected error occurred');
    }
  };

  const onSubmit = async (values) => {
    let body = values;
    addCarHouseCreditHandler(body, selectedCarHouse.id);
    setOpenCreditModal(false);
  };

  const onSearch = () => {
    const values = form.getFieldsValue();

    const cleanedValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    dispatch(carHouseActions.setSearchParams(cleanedValues));
    dispatch(carHouseActions.setCurrentPage(1));
  };

  const onReset = () => {
    form.resetFields();
    dispatch(carHouseActions.setSearchParams({}));
    dispatch(carHouseActions.setCurrentPage(1));
  };

  let content;
  isLoading
    ? (content = <Spinner />)
    : (content = (
        <div className="p-0">
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 1,
              md: 2,
              lg: 3,
              xl: 3,
              xxl: 4,
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              onChange: (page) => {
                dispatch(carHouseActions.setCurrentPage(page));
              },
              showQuickJumper: true,
            }}
            style={{ width: '100%' }}
            dataSource={carHouses}
            renderItem={(item) => (
              <CarHouseItem
                carHouse={item}
                deleteCarHouse={deleteCarHouse}
                onEditUser={onEditCarHouse}
                addCredit={onAddCarHouseCredit}
              />
            )}
          />
        </div>
      ));

  return (
    <>
      <PageHeader
        title={t('carHouses.title')}
        onAdd={onAddCarHouse}
        showButton
        buttonText={t('carHouses.addCarHouse')}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={onSearch}
        style={{ marginBottom: '1rem', maxWidth: '700px' }}
        className="searchForm"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={12}>
            <Form.Item name="name" label={t('common.name')}>
              <Input allowClear placeholder={t('common.searchByName')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Form.Item name="paymentType" label={t('carHouses.paymentType')}>
              <Select allowClear placeholder={t('carHouses.selectPaymentType')}>
                <Option value="prepaid">{t('carHouses.prepaid')}</Option>
                <Option value="postpaid">{t('carHouses.postpaid')}</Option>
                <Option value="monthly">{t('carHouses.monthly')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end">
          <Col>
            <Button
              type="primary"
              shape="round"
              icon={<SearchOutlined />}
              htmlType="submit"
              style={{ marginRight: 8 }}
            >
              {t('common.search')}
            </Button>
            <Button
              type="default"
              shape="round"
              icon={<ReloadOutlined />}
              onClick={onReset}
            >
              {t('common.reset')}
            </Button>
          </Col>
        </Row>
      </Form>
      {content}
      <CarHouseForm
        open={open}
        selectedCarHouse={selectedCarHouse}
        addCarHouse={addCarHouseHandler}
        editCarHouse={editCarHouseHandler}
        onCancel={() => {
          setOpen(false);
        }}
      />

      <CreditModal
        open={openCreditModal}
        onCancel={() => setOpenCreditModal(false)}
        onSubmit={onSubmit}
        selectedCarHouse={selectedCarHouse}
        creditForm={creditForm}
      />
    </>
  );
};

export default CarHouses;
